import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { ChipStatus, DialogForm, IDialogActions, ISnackbarAlert, SnackbarAlert, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { EnvironmentDTO, EnvironmentFilterDTO, ReleaseAppDTO, ReleaseAppFilterDTO, TestAgentDTO, TestAgentFilterDTO, TestComponentDTO, TestComponentFilterDTO, TestExecutionDTO, UserDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { dateDiff, formatDateTime } from '../../../shared/utils';

interface ITestExecutionViewProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: true) => void;
}

export const TestExecutionView: React.FC<ITestExecutionViewProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [executionStatusList, setExecutionStatusList] = useState<ValueListDTO[]>([]);
    const [testComponentList, setTestComponentList] = useState<TestComponentDTO[]>([]);
    const [testAgentList, setTestAgentList] = useState<TestAgentDTO[]>([]);
    const [releaseAppList, setReleaseAppList] = useState<ReleaseAppDTO[]>([]);
    const [environmentList, setEnvironmentList] = useState<EnvironmentDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [testExecutionDTO, setTestExecutionDTO] = useState<TestExecutionDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setTestExecutionDTO({ 
            ...testExecutionDTO, 
            testComponentId: parentId ?? '' 
        } as TestExecutionDTO);

        if(isOpenMaintenance){
            handleTestComponentGetAllAsync();
            handleStatusExecutionGetAllAsync();
            handleEnvironmentGetAllAsync();
            handleReleaseAppGetAllAsync();
            handleTestAgentGetAllAsync();
            handleUserGetAll();
            handleGetByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleUserGetAll = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleStatusExecutionGetAllAsync = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            value: '',
            listId: ListId.executionStatus,
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setExecutionStatusList, showMessage);
    }, []);

    const handleTestComponentGetAllAsync = useCallback(async () => {
        const testComponentFilterDTO: TestComponentFilterDTO = {
            name: '',
            testScenarioId: '',
        };
        GenericService.GetAllAsync1('testingsphere.testcomponent', testComponentFilterDTO, setLoading, setTestComponentList, showMessage);
    }, []);

    const handleEnvironmentGetAllAsync = useCallback(async () => {
        const environmentFilterDTO: EnvironmentFilterDTO = {
            name: '',
        };
        // GenericService.GetAllAsync1('appsphere.environment', environmentFilterDTO, setLoading, setEnvironmentList, showMessage);
    }, []);

    const handleReleaseAppGetAllAsync = useCallback(async () => {
        const releaseAppFilterDTO: ReleaseAppFilterDTO = {
            name: '',
        };
        // GenericService.GetAllAsync1('appsphere.releaseapp', releaseAppFilterDTO, setLoading, setReleaseAppList, showMessage);
    }, []);

    const handleTestAgentGetAllAsync = useCallback(async () => {
        const testAgentFilterDTO: TestAgentFilterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('testingsphere.testagent', testAgentFilterDTO, setLoading, setTestAgentList, showMessage);
    }, []);

    const handleGetByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('testingsphere.testexecution', data.id, setLoading, setTestExecutionDTO, showMessage);
    }, [data]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'close', text: 'Close', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='TEST EXECUTION DETAILS'
                icon='integration_instructions_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <TextFieldReadOnly
                                id={'testComponentId'}
                                label='TestComponent'
                                value={String(testComponentList.find(x => x.id === testExecutionDTO?.testComponentId)?.name)}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'environmentId'}
                                label='Environment'
                                value={String(environmentList.find(x => x.id === testExecutionDTO?.environmentId)?.name)}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'releaseAppId'}
                                label='Release'
                                value={String(releaseAppList.find(x => x.id === testExecutionDTO?.releaseAppId)?.name)}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldReadOnly
                                id={'testAgentId'}
                                label='Test Agent'
                                value={String(testAgentList.find(x => x.id === testExecutionDTO?.testAgentId)?.name)}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'executionStatusId'}
                                label='Status Execution'
                                value={(<ChipStatus label='Execution Status' value={String(executionStatusList.find(x => x.id === testExecutionDTO?.executionStatusId)?.value || 'No Run')} />)}
                                hasDivider={false}
                            />
                        </Grid>
                        {testExecutionDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'startExecutionDateTime'}
                                        label='Start Execution'
                                        value={formatDateTime(String(testExecutionDTO.startExecutionDateTime))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'endExecutionDateTime'}
                                        label='End Execution'
                                        value={formatDateTime(String(testExecutionDTO.endExecutionDateTime))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'executionTime'}
                                        label='Execution Time'
                                        value={testExecutionDTO.startExecutionDateTime ? dateDiff(String(testExecutionDTO.startExecutionDateTime), String(testExecutionDTO.endExecutionDateTime)) : undefined}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={testExecutionDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === testExecutionDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(testExecutionDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === testExecutionDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(testExecutionDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};